import { useState, type FormEvent } from 'react'
import { Star, Trash2 } from 'lucide-react'
import {
  useAddTeamMember,
  useCreateTeam,
  useCreateUser,
  useResendInvite,
  useDeleteTeam,
  useRemoveTeamMember,
  useSetTeamLead,
  useTeams,
  useUpdateTeam,
  useUpdateUser,
  useUploadUserPhoto,
  useUsers,
} from '@/features/team/useTeam'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import Select from '@/components/ui/Select'
import ConfirmButton from '@/components/ui/ConfirmButton'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import Modal from '@/components/ui/Modal'
import OwnerAvatar from '@/components/OwnerAvatar'
import { UserPlus as UserPlusIcon } from 'lucide-react'
import EmailIdentityEditor from '@/features/team/EmailIdentityEditor'
import type { User } from '@/api/users'
import type { Team as TeamRecord } from '@/api/teams'

const PERMISSIONS = [
  { value: 'MANAGE_PIPELINE', label: 'Can edit pipeline & templates' },
  { value: 'SEND_BULK', label: 'Can send bulk messages' },
] as const

const PERMISSION_LABEL: Record<string, string> = Object.fromEntries(
  PERMISSIONS.map((p) => [p.value, p.label]),
)

const emptyUser = { email: '', fullName: '', password: '', role: 'AGENT', phone: '', smsNumber: '', permissions: [] as string[] }

const inputCls = 'px-3 py-2 border border-gray-200 rounded-lg text-sm'

function togglePermission(perms: string[], value: string): string[] {
  return perms.includes(value) ? perms.filter((p) => p !== value) : [...perms, value]
}


export default function Team() {
  const { data: users } = useUsers()
  const create = useCreateUser()
  const [form, setForm] = useState(emptyUser)
  const [editUserId, setEditUserId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [section, setSection] = useState<'people' | 'teams'>('people')
  const [search, setSearch] = useState('')

  // Invite by email (they set their own password) unless the admin chooses to set one.
  const [byInvite, setByInvite] = useState(true)
  const [invitedNote, setInvitedNote] = useState<string | null>(null)
  const resend = useResendInvite()
  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const input = byInvite ? { ...form, password: '' } : form
    create.mutate(input, {
      onSuccess: (u) => {
        setForm(emptyUser)
        setAdding(false)
        setInvitedNote(byInvite ? `Invite sent to ${u.email} — they’ll set their own password from the link.` : null)
      },
    })
  }

  const editUser = (users ?? []).find((u) => u.id === editUserId) ?? null
  const allUsers = users ?? []
  const q = search.trim().toLowerCase()
  const shownUsers = q
    ? allUsers.filter((u) => u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    : allUsers

  return (
    <PageContainer width="full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-darkest">Team</h1>
          <p className="text-sm text-gray-500">
            Agents see only their own deals and contacts (and their team's, if they lead one); admins see everything.
          </p>
          {invitedNote && <p className="text-sm text-success mt-1">{invitedNote}</p>}
        </div>
        <button
          type="button"
          onClick={() => { create.reset(); setAdding(true) }}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
        >
          <UserPlusIcon size={15} /> Add member
        </button>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {(['people', 'teams'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSection(t)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${section === t ? 'border-brand-primary text-brand-primary font-medium' : 'border-transparent text-gray-500 hover:text-brand-dark'}`}
          >
            {t === 'people' ? `People · ${allUsers.length}` : 'Teams'}
          </button>
        ))}
      </div>

      {section === 'people' && allUsers.length > 8 && (
        <div className="mb-3 max-w-xs">
          <input
            className={`${inputCls} w-full`}
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {section === 'people' && (
      <div className="mb-6">
        <TableCard>
          <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Permissions</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {shownUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-400">
                  {q ? `No team members match “${search.trim()}”.` : 'No team members yet.'}
                </td>
              </tr>
            )}
            {shownUsers.map((u) => (
              <tr key={u.id} className="border-t border-gray-100">
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-2 font-medium text-brand-darkest">
                    <OwnerAvatar name={u.fullName} photoUrl={u.photoUrl} size="md" /> {u.fullName}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-gray-600">{u.email}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{u.role === 'AGENCY_ADMIN' ? 'Admin' : u.role === 'AGENT' ? 'Agent' : u.role}</span>
                </td>
                <td className="px-4 py-2.5">
                  {u.invitePending ? (
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span className="text-xs rounded-full px-2 py-0.5 bg-amber-50 text-amber-700" title="Invited by email, hasn't set a password yet">
                        Invited
                      </span>
                      <button
                        type="button"
                        onClick={() => resend.mutate(u.id, { onSuccess: () => setInvitedNote(`Invite sent again to ${u.email}.`) })}
                        disabled={resend.isPending}
                        className="text-xs text-brand-primary hover:underline disabled:opacity-60"
                      >
                        Resend invite
                      </button>
                    </span>
                  ) : (
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        u.status === 'disabled' ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'
                      }`}
                    >
                      {u.status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(u.permissions ?? []).map((p) => (
                      <span key={p} className="text-[11px] bg-brand-primary/10 text-brand-primary rounded-full px-2 py-0.5">
                        {PERMISSION_LABEL[p] ?? p}
                      </span>
                    ))}
                    {(u.permissions ?? []).length === 0 && <span className="text-xs text-gray-300">—</span>}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => setEditUserId(u.id)}
                    className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </TableCard>
      </div>
      )}

      {editUser && (
        <Modal title={`Edit ${editUser.fullName}`} onClose={() => setEditUserId(null)} size="lg">
          <UserEditForm key={editUser.id} user={editUser} onDone={() => setEditUserId(null)} />
        </Modal>
      )}

      {adding && (
      <Modal title="Add a team member" icon={<UserPlusIcon size={17} className="text-brand-primary" />} onClose={() => setAdding(false)} size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-gray-500">Full name
            <input className={`${inputCls} w-full mt-1`} value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} />
          </label>
          <label className="block text-xs font-medium text-gray-500">Email — they sign in with it
            <input className={`${inputCls} w-full mt-1`} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <label className="block text-xs font-medium text-gray-500">Role
            <Select size="md" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="w-full mt-1">
              <option value="AGENT">Agent — sees their own deals and contacts</option>
              <option value="AGENCY_ADMIN">Admin — sees and manages everything</option>
            </Select>
          </label>
          <label className="block text-xs font-medium text-gray-500">Work phone (optional)
            <input className={`${inputCls} w-full mt-1`} placeholder="e.g. 0412 345 678" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </label>
          <label className="block text-xs font-medium text-gray-500 sm:col-span-2">Their SMS number in ProfitPipe (optional)
            <input className={`${inputCls} w-full mt-1`} placeholder="e.g. +61 485 027 976 — the number clients text back to" value={form.smsNumber} onChange={(e) => setForm((f) => ({ ...f, smsNumber: e.target.value }))} />
          </label>
        </div>
        <div className="rounded-lg border border-gray-200 p-3 space-y-2" role="radiogroup" aria-label="How they get in">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="radio" className="mt-1" checked={byInvite} onChange={() => setByInvite(true)} />
            <span><b className="font-medium">Email them an invite</b> <span className="text-gray-500">(recommended) — they get a link and choose their own password.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="radio" className="mt-1" checked={!byInvite} onChange={() => setByInvite(false)} />
            <span><b className="font-medium">Set a password myself</b> <span className="text-gray-500">and pass it on.</span></span>
          </label>
          {!byInvite && (
            <>
              <input className={`${inputCls} w-full`} placeholder="Password (min 10, letters & numbers)" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              <PasswordStrengthMeter password={form.password} />
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-4">
          {PERMISSIONS.map((p) => (
            <label key={p.value} className="flex items-center gap-2 text-sm text-brand-dark">
              <input
                type="checkbox"
                checked={form.permissions.includes(p.value)}
                onChange={() => setForm((f) => ({ ...f, permissions: togglePermission(f.permissions, p.value) }))}
                className="h-4 w-4"
              />
              {p.label}
            </label>
          ))}
        </div>
        {create.error != null && <p className="text-danger text-xs">{apiErrorMessage(create.error)}</p>}
        <button type="submit" disabled={create.isPending} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
          {create.isPending ? 'Adding…' : byInvite ? 'Send invite' : 'Add member'}
        </button>
      </form>
      </Modal>
      )}

      {section === 'teams' && <TeamsSection users={users ?? []} />}
    </PageContainer>
  )
}

function UserEditForm({ user, onDone }: { user: User; onDone: () => void }) {
  const update = useUpdateUser()
  const [email, setEmail] = useState(user.email)
  const [fullName, setFullName] = useState(user.fullName)
  const [role, setRole] = useState(user.role)
  const [status, setStatus] = useState(user.status)
  const [permissions, setPermissions] = useState<string[]>(user.permissions ?? [])
  const [photoUrl, setPhotoUrl] = useState(user.photoUrl ?? '')
  const [phone, setPhone] = useState(user.phone ?? '')
  const [smsNumber, setSmsNumber] = useState(user.smsNumber ?? '')
  const [emailSignature, setEmailSignature] = useState(user.emailSignature ?? '')
  const [identityOpen, setIdentityOpen] = useState(!!(user.emailSignature || user.photoUrl))
  const uploadPhoto = useUploadUserPhoto()
  const [photoError, setPhotoError] = useState<string | null>(null)

  function uploadPhotoFile(f: File) {
    setPhotoError(null)
    uploadPhoto.mutate(
      { id: user.id, file: f },
      {
        onSuccess: (u) => setPhotoUrl(u.photoUrl ?? ''),
        onError: (err) => setPhotoError(apiErrorMessage(err)),
      },
    )
  }

  function save(e: FormEvent) {
    e.preventDefault()
    update.mutate(
      {
        id: user.id,
        input: {
          email: email.trim(),
          fullName,
          role,
          status,
          permissions,
          // empty string clears the value on the backend
          emailSignature: emailSignature.trim(),
          photoUrl: photoUrl.trim(),
          phone: phone.trim(),
          smsNumber: smsNumber.trim(),
        },
      },
      { onSuccess: onDone },
    )
  }

  return (
    <form onSubmit={save} className="space-y-3">
      <div className="text-sm text-gray-500">{user.email}</div>
      <div>
        <input
          className={`${inputCls} w-full`}
          type="email"
          placeholder="Email (login)"
          aria-label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="text-[11px] text-gray-400 mt-1">The email the user signs in with — must be unique in this workspace.</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input className={inputCls} placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Select size="md" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="AGENT">Agent</option>
          <option value="AGENCY_ADMIN">Admin</option>
        </Select>
        <Select size="md" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">active</option>
          <option value="disabled">disabled</option>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className={`${inputCls} w-full`} placeholder="Work phone (shown on the owner card)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <input className={`${inputCls} w-full`} placeholder="SMS number, E.164 (e.g. +61400000000)" value={smsNumber} onChange={(e) => setSmsNumber(e.target.value)} />
      </div>
      <p className="text-[11px] text-gray-400 -mt-1">
        <b>SMS number</b> — the dedicated business number texts are sent from. <b>Work phone</b> — the agent's
        own phone: client SMS replies are forwarded here and they're notified when a text comes in.
      </p>
      <div className="flex flex-wrap gap-4">
        {PERMISSIONS.map((p) => (
          <label key={p.value} className="flex items-center gap-2 text-sm text-brand-dark">
            <input
              type="checkbox"
              checked={permissions.includes(p.value)}
              onChange={() => setPermissions((prev) => togglePermission(prev, p.value))}
              className="h-4 w-4"
            />
            {p.label}
          </label>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">Admins can always do everything; permissions only apply to agents.</p>

      <details
        open={identityOpen}
        onToggle={(e) => setIdentityOpen(e.currentTarget.open)}
        className="border-t border-gray-100 pt-3"
      >
        <summary className="text-xs font-medium text-gray-500 cursor-pointer select-none hover:text-brand-dark">
          Email identity — photo &amp; signature
        </summary>
        <div className="mt-3">
          <EmailIdentityEditor
            photoUrl={photoUrl}
            onPhotoUrlChange={setPhotoUrl}
            signature={emailSignature}
            onSignatureChange={setEmailSignature}
            onUpload={uploadPhotoFile}
            uploading={uploadPhoto.isPending}
            photoError={photoError}
          />
        </div>
      </details>

      {update.error != null && <p className="text-danger text-xs">{apiErrorMessage(update.error)}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={update.isPending} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onDone} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  )
}

function TeamsSection({ users }: { users: User[] }) {
  const { data: teams } = useTeams()
  const createTeam = useCreateTeam()
  const [name, setName] = useState('')

  function onCreate(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    createTeam.mutate(
      { name: name.trim() },
      { onSuccess: () => setName('') },
    )
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-brand-darkest mb-1">Teams</h2>
      <p className="text-sm text-gray-500 mb-3">
        Group agents into teams and star one or more leads — this is what controls who can see whose records.
      </p>

      <div className="mb-5 rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-4 text-sm space-y-2">
        <div className="font-medium text-brand-darkest">How team visibility works</div>
        <ul className="list-disc pl-5 space-y-1 text-gray-600">
          <li><span className="font-medium text-brand-dark">Agents</span> see only the deals and contacts they own.</li>
          <li>A <span className="font-medium text-brand-dark">team lead</span> (star a member — a team can have several) also sees the deals and contacts owned by everyone in the teams they lead.</li>
          <li><span className="font-medium text-brand-dark">Admins</span> always see everything in the workspace.</li>
        </ul>
        <p className="text-gray-500 pt-0.5">
          Example: star two sales managers as leads of a team of 5 agents — both can now see all 5 agents'
          pipelines and contacts, while each agent still sees only their own. Being on a team changes only
          who can <span className="italic">see</span> a record, never who owns or is assigned to it.
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {(teams ?? []).length === 0 && <p className="text-sm text-gray-400">No teams yet.</p>}
        {(teams ?? []).map((t) => (
          <TeamCard key={t.id} team={t} users={users} />
        ))}
      </div>

      <form onSubmit={onCreate} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <div className="text-sm font-semibold text-brand-darkest">Create a team</div>
        <p className="text-xs text-gray-500">Name the team, then add members and star who leads it.</p>
        <div className="flex gap-2">
          <input className={`${inputCls} flex-1`} placeholder="Team name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        {createTeam.error != null && <p className="text-danger text-xs">{apiErrorMessage(createTeam.error)}</p>}
        <button type="submit" disabled={createTeam.isPending || !name.trim()} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
          {createTeam.isPending ? 'Creating…' : 'Create team'}
        </button>
      </form>
    </div>
  )
}

function TeamCard({ team, users }: { team: TeamRecord; users: User[] }) {
  const update = useUpdateTeam()
  const del = useDeleteTeam()
  const addMember = useAddTeamMember()
  const removeMember = useRemoveTeamMember()
  const setLead = useSetTeamLead()

  const [name, setName] = useState(team.name)
  const [newMemberId, setNewMemberId] = useState('')

  const memberIds = new Set(team.members.map((m) => m.id))
  const candidates = users.filter((u) => !memberIds.has(u.id))
  const dirty = name !== team.name
  const error = update.error ?? del.error ?? addMember.error ?? removeMember.error ?? setLead.error

  function save() {
    if (!name.trim()) return
    update.mutate({ id: team.id, input: { name: name.trim() } })
  }

  function remove() {
    del.mutate(team.id)
  }

  function onAddMember() {
    if (!newMemberId) return
    addMember.mutate({ teamId: team.id, userId: newMemberId }, { onSuccess: () => setNewMemberId('') })
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-2">
        <input className={`${inputCls} flex-1`} value={name} onChange={(e) => setName(e.target.value)} />
        {dirty && (
          <button
            onClick={save}
            disabled={update.isPending || !name.trim()}
            className="px-3 py-2 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
        <ConfirmButton onConfirm={remove} disabled={del.isPending} title="Delete team">
          <Trash2 size={16} />
        </ConfirmButton>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Members</p>
          <p className="text-[11px] text-gray-400 flex items-center gap-1">
            <Star size={11} className="text-amber-500 fill-current" /> = lead (sees the whole team)
          </p>
        </div>
        {team.members.length === 0 && <p className="text-xs text-gray-400 mb-1.5">No members yet.</p>}
        <div className="mb-2 divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {team.members.map((m) => (
            <div key={m.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-gray-50">
              <button
                onClick={() => setLead.mutate({ teamId: team.id, userId: m.id, lead: !m.lead })}
                disabled={setLead.isPending}
                title={m.lead ? 'Remove as team lead' : 'Make team lead'}
                aria-pressed={m.lead}
                className={`shrink-0 disabled:opacity-50 ${m.lead ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}
              >
                <Star size={16} className={m.lead ? 'fill-current' : ''} />
              </button>
              <span className="flex-1 truncate text-brand-dark">{m.fullName}</span>
              {m.lead && (
                <span className="shrink-0 text-[10px] uppercase tracking-wide rounded-full bg-brand-primary/10 text-brand-primary px-1.5 py-0.5">
                  Lead
                </span>
              )}
              <button
                onClick={() => removeMember.mutate({ teamId: team.id, userId: m.id })}
                disabled={removeMember.isPending}
                className="shrink-0 text-gray-300 hover:text-danger disabled:opacity-50"
                title="Remove from team"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Select size="xs" value={newMemberId} onChange={(e) => setNewMemberId(e.target.value)}>
            <option value="">Add member…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>{u.fullName}</option>
            ))}
          </Select>
          <button
            onClick={onAddMember}
            disabled={!newMemberId || addMember.isPending}
            className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            Add
          </button>
        </div>
      </div>

      {error != null && <p className="text-danger text-xs">{apiErrorMessage(error)}</p>}
    </div>
  )
}
